import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, Clock, MapPinOff, PlayCircle } from "lucide-react";

const META = {
  por_empezar: { Ic: PlayCircle, color: "#d97706", label: "Recordatorio de ronda" },
  atrasada: { Ic: Clock, color: "#BF3437", label: "Desvío de tiempo" },
  punto_omitido: { Ic: MapPinOff, color: "#BF3437", label: "Punto omitido" },
};

// Polls /api/patrols/alerts every 45s. Plays a beep when new alerts appear.
export function PatrolAlertBanner() {
  const [alerts, setAlerts] = useState([]);
  // Fix D (2026-08-30): por defecto colapsado — solo se ve la alerta más urgente.
  const [expandido, setExpandido] = useState(false);
  const lastKeyRef = useRef("");
  const ctxRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onFirstInteract = () => {
      try { ctxRef.current = new (window.AudioContext || window.webkitAudioContext)(); } catch {}
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
    window.addEventListener("click", onFirstInteract, { once: true });
    window.addEventListener("keydown", onFirstInteract, { once: true });

    const poll = async () => {
      try {
        const { data } = await api.get("/patrols/alerts");
        const list = data.alerts || [];
        const key = list.map((a) => `${a.patrol_id}:${a.type}`).sort().join("|");
        // Fix D (2026-08-30): el beep solo cuando aparece una alerta NUEVA de
        // severidad alta, no cada vez que cambia el conjunto. Con las rondas
        // acumuladas el teléfono sonaba sin parar toda la noche.
        const nuevasAltas = list.filter((a) => a.severity === "high"
          && !lastKeyRef.current.includes(`${a.patrol_id}:${a.type}`));
        if (nuevasAltas.length) playBeep(ctxRef.current);
        lastKeyRef.current = key;
        setAlerts(list);
      } catch {}
    };
    poll();
    const i = setInterval(poll, 45000);
    return () => {
      clearInterval(i);
      window.removeEventListener("click", onFirstInteract);
      window.removeEventListener("keydown", onFirstInteract);
    };
  }, []);

  if (alerts.length === 0) return null;

  // Fix D (2026-08-30): antes se renderizaba UNA barra sticky por cada alerta.
  // Con rondas automáticas cada 30-60 min, en una noche se acumulaban 8-12 barras
  // que tapaban la pantalla entera del vigilador y le impedían trabajar (video del
  // 29/08, Kraft Constructora). Ahora se muestra SOLO la más urgente, con un
  // contador del resto que se puede desplegar.
  const ordenadas = [...alerts].sort((a, b) => {
    const sev = { high: 0, medium: 1, low: 2 };
    const d = (sev[a.severity] ?? 3) - (sev[b.severity] ?? 3);
    if (d !== 0) return d;
    return String(a.scheduled_for || "").localeCompare(String(b.scheduled_for || ""));
  });
  const visibles = expandido ? ordenadas : ordenadas.slice(0, 1);
  const ocultas = ordenadas.length - visibles.length;

  return (
    <div data-testid="patrol-alert-banner">
      {visibles.map((a, idx) => {
        const meta = META[a.type] || META.por_empezar;
        const Ic = meta.Ic;
        return (
          <div key={`${a.patrol_id}-${a.type}-${idx}`}
            style={{
              position: "sticky", top: 0, zIndex: 70,
              background: meta.color, color: "white", padding: "12px 18px",
              display: "flex", alignItems: "center", gap: 12,
              fontWeight: 800, letterSpacing: "0.03em",
              animation: a.severity === "high" ? "pulse-red 2s infinite" : "none",
              borderBottom: "1px solid rgba(255,255,255,0.15)",
            }}
            data-testid={`patrol-alert-${a.type}`}>
            <Ic size={20} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.85, letterSpacing: "0.1em" }}>{meta.label}</div>
              <div style={{ fontSize: 13 }}>{a.message}</div>
            </div>
            <button
              onClick={() => nav(`/vigilador/ronda/${a.patrol_id}`)}
              style={{ background: "rgba(0,0,0,0.2)", border: "1px solid rgba(255,255,255,0.4)", color: "#fff", padding: "7px 12px", borderRadius: 6, fontSize: 11, fontWeight: 800, cursor: "pointer", whiteSpace: "nowrap" }}
              data-testid={`patrol-alert-view-${a.patrol_id}`}>
              Ver ronda →
            </button>
          </div>
        );
      })}
      {ocultas > 0 && (
        <button
          onClick={() => setExpandido(true)}
          style={{
            position: "sticky", top: 0, zIndex: 69, width: "100%",
            background: "#1a1512", color: "#f3d9d9", border: "none",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            padding: "5px 18px", fontSize: 11, fontWeight: 800,
            letterSpacing: "0.04em", textAlign: "left", cursor: "pointer",
            lineHeight: "16px",
          }}
          data-testid="patrol-alert-more">
          +{ocultas} ronda{ocultas === 1 ? "" : "s"} pendiente{ocultas === 1 ? "" : "s"} más ▾
        </button>
      )}
      {expandido && ordenadas.length > 1 && (
        <button
          onClick={() => setExpandido(false)}
          style={{
            position: "sticky", top: 0, zIndex: 69, width: "100%",
            background: "#1a1512", color: "#f3d9d9", border: "none",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            padding: "5px 18px", fontSize: 11, fontWeight: 800,
            letterSpacing: "0.04em", textAlign: "left", cursor: "pointer",
            lineHeight: "16px",
          }}
          data-testid="patrol-alert-collapse">
          Mostrar solo la más urgente ▴
        </button>
      )}
    </div>
  );
}

function playBeep(ctx) {
  if (!ctx) return;
  try {
    [880, 660, 880].forEach((freq, i) => {
      setTimeout(() => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.type = "square"; osc.frequency.value = freq;
        gain.gain.value = 0.15;
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(); setTimeout(() => osc.stop(), 200);
      }, i * 250);
    });
  } catch {}
}
